import { ref } from 'vue';
import { useQueryClient } from '@tanstack/vue-query';
import useAuthStore from '~/stores/auth';

/**
 * What "refresh" means when the user asks for it.
 *
 * ## Why this exists at all
 *
 * On the web this is the F5 key. A page reload discards Pinia — which is memory-only — so
 * the profile and subscriptions are re-fetched on the next navigation, and TanStack Query
 * starts cold. Nobody ever had to think about it.
 *
 * The packaged app has no reload. One JavaScript context lives for the whole life of the app
 * process, backgrounding does not clear it, and on iOS the user would have to swipe the app
 * out of the switcher to get a cold start — which nobody does. So an admin changing someone's
 * permissions or profile had no way of reaching them. This is the missing F5.
 *
 * Mobile web already has it: Safari and Chrome both pull-to-refresh into a real page load.
 * The gap is the app specifically, not mobile.
 *
 * ## Why the subscriptions call is debounced and the rest is not
 *
 * `user-subscriptions` is the most expensive endpoint the API exposes — it verifies against
 * the payment provider in real time on every call, which is exactly what makes it accurate.
 * Its maintainer asked us to treat it as one call per user-initiated refresh, never a
 * heartbeat.
 *
 * So an impatient user yanking the list four times gets four cache invalidations and four
 * profile reads, and **one** payment-provider round trip. The gesture stays responsive; the
 * expensive part does not repeat. See docs/api-reply-cache-freshness.md.
 */

/** Minimum gap between payment-provider round trips, however often the gesture fires. */
const SUBSCRIPTIONS_MIN_INTERVAL_MS = 30_000;

/**
 * Module-level so the interval is shared by every caller. A per-instance value would reset
 * whenever the component remounted, which is on every route change.
 */
let lastSubscriptionsRefreshAt = 0;

/** Long enough that the spinner reads as a deliberate response rather than a flicker. */
const MIN_SPINNER_MS = 450;

export function useAppRefresh() {
   const isRefreshing = ref(false);
   const queryClient = useQueryClient();

   /**
    * Re-reads everything a user could reasonably expect a refresh to update.
    *
    * Resolves only once the work is done, so the caller can keep a spinner up for exactly as
    * long as it takes. Never rejects: a failed refresh should leave the previous data on
    * screen, not replace a working page with an error because someone pulled a list.
    */
   const refresh = async () => {
      if (isRefreshing.value) return;

      isRefreshing.value = true;

      const startedAt = Date.now();

      try {
         await Promise.all([refreshQueries(), refreshProfile(), refreshSubscriptions()]);
      } finally {
         await holdSpinner(startedAt);

         isRefreshing.value = false;
      }
   };

   /**
    * Everything mounted, in one call. A permission change moves more than one query —
    * per-course access flags, bundle contents, blog and video access — and Vue Query only
    * refetches what is actually on screen, so the blanket call costs what the current page
    * costs. `usePortalSwitcher` and the Stripe return take the same approach.
    *
    * This is also what drops cached media URLs. They are unsigned and never expire, so a URL
    * already handed out keeps working; clearing the responses that carry them is the only
    * part of that we control.
    */
   const refreshQueries = async () => {
      await queryClient.invalidateQueries();
   };

   const refreshProfile = async () => {
      const authStore = useAuthStore();

      if (!authStore.accessToken) return;

      await authStore.setCurrentUserProfile({ force: true }).catch(() => undefined);
   };

   const refreshSubscriptions = async () => {
      const authStore = useAuthStore();

      if (!authStore.accessToken) return;

      if (Date.now() - lastSubscriptionsRefreshAt < SUBSCRIPTIONS_MIN_INTERVAL_MS) return;

      lastSubscriptionsRefreshAt = Date.now();

      await authStore.setCurrentUserSubscriptions({ force: true }).catch(() => undefined);
   };

   const holdSpinner = async (startedAt: number) => {
      const elapsed = Date.now() - startedAt;

      if (elapsed >= MIN_SPINNER_MS) return;

      await new Promise((resolve) => setTimeout(resolve, MIN_SPINNER_MS - elapsed));
   };

   return { isRefreshing, refresh };
}

/** Test seam. Nothing in the application should need this. */
export function resetAppRefreshThrottle() {
   lastSubscriptionsRefreshAt = 0;
}
